/*
 * ProfileImagePicker used in profile & admin pages
 */

// packages
import AvatarEditor, { AvatarEditorRef } from 'react-avatar-editor';
import React, { useState, useRef, useCallback } from 'react';
import { Box, Typography, Button, Slider } from '@mui/material';
import { putFile } from '../../../../lib/API/file';
import { gql } from '@apollo/client';
import Globals from '../../../../lib/Globals';
import { useToast } from '../../../../hooks/useToast';
import {
	useProfileImagePicker_RequestOrgProfileImageUploadUrlLazyQuery,
	useProfileImagePicker_UpdateOrganizationMutation,
	UserType,
} from '#types';

import styles from './ProfileImagePicker.module.scss';
import { Icon } from '../../../../blocks/icons';
import { Droppable } from '../Droppable';

// logic
import { dataURIToFile, fileNameToMimeType } from '../../../../lib/helpers';
import { FlattenedUser } from '../../../../types/user';
import { useSelector } from 'react-redux';

type EditorConfig = {
	color: [number, number, number, number];
	border: number;
	width: number;
	height: number;
	borderRadius: number;
};

export type ProfileImagePickerValues = {
	imageScale?: number;
	imageRotation?: number;
	imagePositionX?: number;
	imagePositionY?: number;
	originalImage?: File | string;
};

type ProfileImagePickerProps = {
	closeModal?: () => void;
	onSuccess: ( values: ProfileImagePickerValues ) => void;
};

type PositionChangeEvent = {
	x: number;
	y: number;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const updateOrganizationMutation = gql`
	mutation ProfileImagePicker_updateOrganization(
		$where: OrganizationWhereUniqueInput!
		$data: OrganizationCustomUpdateInput!
	) {
		updateOrganization(where: $where, data: $data) {
			id
			name
			services {
				id
				name
			}
			image
			imageScale
			imageRotation
			imagePositionX
			imagePositionY
			updatedAt
		}
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const profileImagePicker_requestOrgProfileImageUploadUrl = gql`
	query ProfileImagePicker_requestOrgProfileImageUploadUrl(
		$data: RequestImageUploadUrlInput!
	) {
		requestOrgProfileImageUploadUrl(data: $data) {
			uploadURL
		}
	}
`;

const ProfileImagePicker: React.FC<ProfileImagePickerProps> = ( {
	closeModal,
	onSuccess,
} ) => {
	const user: FlattenedUser = useSelector( ( state: any ) => state.user );
	const organization =
		user?.isLoggedIn && user.userType === UserType.OrgUser
			? user.organization
			: undefined;
	const editorRef = useRef<AvatarEditorRef>( null );
	const { showError } = useToast();
	const [ editorConfig ] = useState<EditorConfig>( {
		color: [
			255,
			255,
			255,
			0.6
		],
		border: 20,
		width: 340,
		height: 340,
		borderRadius: 170,
	} );

	const [ imageScale, setImageScale ] = useState<number>( 2.5 );
	const [ imageRotation, setImageRotation ] = useState<number>( 0 );
	const [ image, setImage ] = useState<File | string | undefined>( undefined );
	const [ imagePositionX, setImagePositionX ] = useState<number>( 0 );
	const [ imagePositionY, setImagePositionY ] = useState<number>( 0 );
	const [ hasPickedImage, setHasPickedImage ] = useState<boolean>( false );

	const onPositionChange = useCallback( ( e: PositionChangeEvent ) => {
		setImagePositionX( e.x );
		setImagePositionY( e.y );
	}, [] );
	const [ requestOrgProfileImageUploadUrl, { loading: requestUploadUrlLoading }, ] = useProfileImagePicker_RequestOrgProfileImageUploadUrlLazyQuery();
	const [ updateOrganization, { loading: updateOrganizationLoading } ] =
		useProfileImagePicker_UpdateOrganizationMutation();

	const saveImageSettings = useCallback( async () => {
		if ( !editorRef.current ) {
			return;
		}

		// Create the cropped canvas image first (needed for both onboarding and update flows)
		const data = editorRef.current.getImageScaledToCanvas().toDataURL();

		let name = '';
		if ( image && typeof image === 'object' && 'name' in image ) {
			name = image.name;
		} else if ( typeof image === 'string' ) {
			const imagePathParts = image.split( '/' );
			name = imagePathParts[ imagePathParts.length - 1 ]?.split( '?' )[ 0 ] || '';
		}

		const mimeType = fileNameToMimeType( name );
		const imageFile = await dataURIToFile( data, name, mimeType );

		if ( imageFile?.size > Globals.maxAttachmentSize ) {
			showError( 'Profile images must be smaller than 10MB.' );
			return;
		}

		// If no organization exists (onboarding flow), return the cropped image file
		if ( !organization ) {
			onSuccess( {
				imagePositionX: Math.round( imagePositionX * 10 ),
				imagePositionY: Math.round( imagePositionY * 10 ),
				imageRotation,
				imageScale: Math.round( imageScale * 10 ),
				originalImage: imageFile, // Return the cropped canvas image, not the original
			} );
			if ( closeModal ) {
				closeModal();
			}
			return;
		}

		// If organization exists (update flow), upload and update
		const urlRes = await requestOrgProfileImageUploadUrl( {
			variables: { data: { fileType: imageFile.type } },
		} );
		if ( urlRes.error ) {
			showError( urlRes.error );
		} else if ( urlRes.data?.requestOrgProfileImageUploadUrl.uploadURL ) {
			const putRes = await putFile(
				imageFile,
				urlRes.data?.requestOrgProfileImageUploadUrl.uploadURL
			);
			if ( 'errors' in putRes && putRes.errors ) {
				showError(
					'Oops, there was an error uploading your image. Please try again.'
				);
			} else if ( 'imageUrl' in putRes ) {
				const image = putRes.imageUrl;
				const updateRes = await updateOrganization( {
					variables: {
						where: { id: organization.id },
						data: {
							image,
							imagePositionX: Math.round( imagePositionX * 10 ),
							imagePositionY: Math.round( imagePositionY * 10 ),
							imageRotation,
							imageScale: Math.round( imageScale * 10 ),
						},
					},
				} );
				if ( updateRes.errors ) {
					updateRes.errors.forEach( ( error ) => showError( error ) );
				} else {
					onSuccess( {
						imagePositionX: Math.round( imagePositionX * 10 ),
						imagePositionY: Math.round( imagePositionY * 10 ),
						imageRotation,
						imageScale: Math.round( imageScale * 10 ),
						originalImage: image,
					} );
				}
			}
		}

		if ( closeModal ) {
			closeModal();
		}
	}, [
		imageRotation,
		imageScale,
		imagePositionX,
		imagePositionY,
		requestOrgProfileImageUploadUrl,
		updateOrganization,
		organization,
		image,
		onSuccess,
		closeModal,
		showError,
	] );

	const reset = useCallback( () => {
		setImageScale( 2.5 );
		setImageRotation( 0 );
	}, [] );

	const handleChange = useCallback( ( name: string, value: string | number ) => {
		if ( name === 'imageScale' ) {
			setImageScale( parseFloat( String( value ) ) );
		} else if ( name === 'imageRotation' ) {
			setImageRotation( parseFloat( String( value ) ) );
		}
	}, [] );

	const uploadPicture = useCallback( ( files: File[] ) => {
		if ( files.length > 0 ) {
			setImage( files[ 0 ] );
			setHasPickedImage( true );
		}
	}, [] );

	const style = {
		width: '100%',
		height: 'auto',
		border: '1px solid #E1E1E1',
		background: 'rgb(225, 225, 225)',
	};

	const acceptedFilesTypes = 'image/jpeg, image/png';

	const minZoom = 1.0;
	const maxZoom = 4.0;
	const stepZoom = 0.1;

	const minRotate = -180;
	const maxRotate = 180;
	const stepRotate = 1;

	return (
		<Box className={ styles.container }>
			{ hasPickedImage && typeof image !== 'undefined' ? (
				<>
					<AvatarEditor
						ref={ editorRef }
						crossOrigin='anonymous'
						onPositionChange={ onPositionChange }
						style={ style }
						{ ...editorConfig }
						scale={ imageScale }
						rotate={ imageRotation }
						image={ image }
					/>
					<Box className={ styles.sliderContainer }>
						<Box>
							<Typography>Adjust zoom level</Typography>
							<Slider
								value={ imageScale }
								step={ stepZoom }
								min={ minZoom }
								max={ maxZoom }
								disabled={ false }
								onChange={ ( event, value ) => {
									const numValue = Array.isArray( value ) ? value[ 0 ] : value;
									handleChange( 'imageScale', numValue );
								} }
							/>
						</Box>
						<Box>
							<Box>
								<Typography>Rotate picture</Typography>
							</Box>
							<Slider
								value={ imageRotation }
								step={ stepRotate }
								min={ minRotate }
								max={ maxRotate }
								disabled={ false }
								onChange={ ( event, value ) => {
									const numValue = Array.isArray( value ) ? value[ 0 ] : value;
									handleChange( 'imageRotation', numValue );
								} }
							/>
						</Box>
					</Box>
					<Box className={ styles.buttonContainer }>
						<Button
							onClick={ saveImageSettings }
							loading={ requestUploadUrlLoading || updateOrganizationLoading }
						>
							Save
						</Button>
						<Button onClick={ reset } variant='outlined'>
							Reset
						</Button>
					</Box>
				</>
			) : (
				<>
					<Droppable
						handleDrop={ uploadPicture }
						acceptedFilesTypes={ acceptedFilesTypes }
						content={
							<Box className={ styles.instructions }>
								<Icon type='upload-file' className={ styles.icon } />
								<Typography>Click here to add a profile picture</Typography>
								<Typography>or drag and drop an image</Typography>
								<Typography>(.png or .jpg)</Typography>
							</Box>
						}
					/>
				</>
			) }
		</Box>
	);
};

export default ProfileImagePicker;
